import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, Play } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function VideoManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", thumbnail_url: "", video_url: "", duration: "", category_id: "" });
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/videos").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try {
      if (editId) await api.put(`/videos/${editId}`, form);
      else await api.post("/videos", form);
      toast.success("Saved"); setOpen(false); setEditId(null); load();
    } catch { toast.error("Failed"); }
  };

  const handleEdit = (item) => {
    setForm({ title: item.title, description: item.description || "", thumbnail_url: item.thumbnail_url || "", video_url: item.video_url || "", duration: item.duration || "", category_id: item.category_id || "" });
    setEditId(item.id); setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/videos/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="video-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-serif font-bold text-foreground">Videos</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" onClick={() => { setForm({ title: "", description: "", thumbnail_url: "", video_url: "", duration: "", category_id: "" }); setEditId(null); setOpen(true); }} data-testid="new-video-btn">
              <Plus className="h-4 w-4 mr-2" /> New Video
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Edit" : "New"} Video</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div><Label className="font-sans text-sm">Title *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} className="mt-1" data-testid="video-title" /></div>
              <div><Label className="font-sans text-sm">Video URL</Label><Input value={form.video_url} onChange={e => set("video_url", e.target.value)} className="mt-1" data-testid="video-url" /></div>
              <div><Label className="font-sans text-sm">Thumbnail URL</Label><Input value={form.thumbnail_url} onChange={e => set("thumbnail_url", e.target.value)} className="mt-1" /></div>
              <div><Label className="font-sans text-sm">Duration</Label><Input value={form.duration} onChange={e => set("duration", e.target.value)} placeholder="12:45" className="mt-1" /></div>
              <div><Label className="font-sans text-sm">Description</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} className="mt-1" /></div>
              <Button onClick={handleSave} className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-video"><Save className="h-4 w-4 mr-2" />Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(vid => (
          <div key={vid.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid={`admin-video-${vid.id}`}>
            <div className="relative aspect-video">
              {vid.thumbnail_url ? <img src={vid.thumbnail_url} alt={vid.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center"><Play className="h-8 w-8 text-muted-foreground" /></div>}
              {vid.duration && <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-sans px-2 py-0.5">{vid.duration}</span>}
            </div>
            <div className="p-4">
              <h3 className="font-sans font-medium text-sm text-foreground line-clamp-2">{vid.title}</h3>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(vid)}><Pencil className="h-3 w-3" /></Button>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(vid.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
